export const DIRECTORY_KINDS = ['client', 'team_member', 'company', 'person'];
/** Columns no universe query may select. `type` is sensitive on `people` (employment type) only. */
export const SENSITIVE_COLUMNS = {
    all: ['*', 'rate', 'contract_hours', 'scope_total_value', 'available', 'flagged_reason'],
    people: ['type'],
};
/** The only columns universe queries select, per table. */
export const UNIVERSE_COLUMNS = {
    clients: 'prefix, name, companies(slug)',
    people: 'id, name, full_name, role, teams, person_id',
    companies: 'slug, name, kind',
    persons: 'id, full_name, email',
    company: 'id, slug, name, kind, description, company_domains(domain)',
    relationships: 'type, started_at',
    affiliations: 'role, persons(id, full_name)',
    clientList: 'id, prefix, name, archived_at, since_month, engagement_type, owner:people!clients_account_owner_id_fkey(name)',
    linkedClient: 'prefix, name',
    profile: 'profile, enriched_at, schema_version',
    person: 'id, full_name, email, linkedin_url',
    personAffiliations: 'role, ended_at, companies(slug, name)',
    personContactOf: 'role, clients(prefix, name)',
    personTeam: 'name, role, teams',
    personPriority: 'priority, priority_source, refresh_days',
};
function rowsOf(r) {
    if (r?.error)
        throw new Error(r.error.message);
    return (r?.data ?? []);
}
function toProfile(row) {
    return row ? { profile: row.profile, enrichedAt: row.enriched_at, schemaVersion: row.schema_version } : null;
}
/** Adds the name/email filter for an already-escaped query; an empty query matches everything. */
function matching(builder, q, cols) {
    return q ? builder.or(cols.map((c) => `${c}.ilike.%${q}%`).join(',')) : builder;
}
/**
 * Non-archived clients, current team members, companies and persons matching an escaped query (see `escapeIlike`),
 * up to `limit` per kind. Lupe uses 8 per kind (tool output), the OS Explorer 12 (one screen of cards).
 */
export async function searchDirectory(db, q, kinds, limit) {
    const want = new Set(kinds);
    const [clients, team, companies, persons] = await Promise.all([
        want.has('client')
            ? matching(db.from('clients').select(UNIVERSE_COLUMNS.clients), q, ['name', 'prefix']).is('archived_at', null).order('name').limit(limit)
            : null,
        want.has('team_member')
            ? matching(db.from('people').select(UNIVERSE_COLUMNS.people), q, ['name', 'full_name', 'email']).is('terminated_month', null).order('name').limit(limit)
            : null,
        want.has('company')
            ? matching(db.from('companies').select(UNIVERSE_COLUMNS.companies), q, ['name', 'slug']).order('name').limit(limit)
            : null,
        want.has('person')
            ? matching(db.from('persons').select(UNIVERSE_COLUMNS.persons), q, ['full_name', 'email']).order('full_name').limit(limit)
            : null,
    ]);
    return {
        clients: rowsOf(clients),
        team_members: rowsOf(team),
        companies: rowsOf(companies),
        persons: rowsOf(persons),
    };
}
/** Every client, newest first (`since_month`), with its account owner; archived (past) clients only when asked. */
export async function listClients(db, opts = {}) {
    const query = db.from('clients').select(UNIVERSE_COLUMNS.clientList);
    const { data, error } = await (opts.includeArchived ? query : query.is('archived_at', null))
        .order('since_month', { ascending: false, nullsFirst: false }).order('name');
    if (error)
        throw new Error(error.message);
    return (data ?? []).map((c) => ({
        id: c.id,
        prefix: c.prefix,
        name: c.name,
        archived: c.archived_at !== null,
        since_month: c.since_month,
        engagement_type: c.engagement_type,
        account_owner: c.owner?.name ?? null,
    }));
}
/** One company by exact slug, or null. */
export async function companyBySlug(db, slug) {
    return rowsOf(await db.from('companies').select(UNIVERSE_COLUMNS.company).eq('slug', slug).limit(1))[0] ?? null;
}
/** A company with its open relationships, current people and linked non-archived client. */
export async function companyDetail(db, c) {
    const [rels, affs, client, profile] = await Promise.all([
        db.from('company_relationships').select(UNIVERSE_COLUMNS.relationships).eq('company_id', c.id).is('ended_at', null),
        db.from('person_affiliations').select(UNIVERSE_COLUMNS.affiliations).eq('company_id', c.id).is('ended_at', null).limit(50),
        db.from('clients').select(UNIVERSE_COLUMNS.linkedClient).eq('company_id', c.id).is('archived_at', null).limit(1),
        db.from('company_profiles').select(UNIVERSE_COLUMNS.profile).eq('company_id', c.id).limit(1),
    ]);
    const people = rowsOf(affs);
    return {
        slug: c.slug,
        name: c.name,
        kind: c.kind,
        description: c.description,
        domains: (c.company_domains ?? []).map((d) => d.domain),
        relationships: rowsOf(rels),
        people: people.flatMap((a) => (a.persons ? [{ id: a.persons.id, full_name: a.persons.full_name, role: a.role }] : [])),
        client: rowsOf(client)[0] ?? null,
        profile: toProfile(rowsOf(profile)[0]),
    };
}
/** One person for the Explorer and Lupe: identity, companies, client contacts, team membership, enrichment profile and refresh priority. */
export async function personDetail(db, id) {
    const { data: p, error } = await db.from('persons').select(UNIVERSE_COLUMNS.person).eq('id', id).maybeSingle();
    if (error)
        throw new Error(error.message);
    if (!p)
        return null;
    const [affs, contacts, team, profile, priority] = await Promise.all([
        db.from('person_affiliations').select(UNIVERSE_COLUMNS.personAffiliations).eq('person_id', id),
        db.from('client_contacts').select(UNIVERSE_COLUMNS.personContactOf).eq('person_id', id),
        db.from('people').select(UNIVERSE_COLUMNS.personTeam).eq('person_id', id).is('terminated_month', null).limit(1),
        db.from('person_profiles').select(UNIVERSE_COLUMNS.profile).eq('person_id', id).limit(1),
        db.from('person_enrichment_priority').select(UNIVERSE_COLUMNS.personPriority).eq('person_id', id).limit(1),
    ]);
    const teamRow = rowsOf(team)[0];
    const prio = rowsOf(priority)[0];
    return {
        id: p.id,
        full_name: p.full_name,
        email: p.email,
        linkedin_url: p.linkedin_url,
        affiliations: rowsOf(affs).flatMap((a) => a.companies ? [{ company: a.companies, role: a.role, current: a.ended_at === null }] : []),
        contactOf: rowsOf(contacts).flatMap((c) => c.clients ? [{ prefix: c.clients.prefix, name: c.clients.name, role: c.role }] : []),
        teamMember: teamRow ? { name: teamRow.name, role: teamRow.role, teams: teamRow.teams ?? [] } : null,
        profile: toProfile(rowsOf(profile)[0]),
        priority: prio ? { priority: prio.priority, source: prio.priority_source, refreshDays: prio.refresh_days } : null,
    };
}
/** Drops characters that break or widen a PostgREST or()/ilike filter. */
export function escapeIlike(q) {
    return q.replace(/[,()%_*\\"]/g, ' ').replace(/\s+/g, ' ').trim();
}
